import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';
import {
    fetchConfigDetail,
    listConfigurations,
    updateConfigurationValue
} from '../../../actions/configurations';
import { configurationUtils } from '@codametrix/ui-common';

const { serviceLineAliases, serviceLineMatches } = configurationUtils;

interface SaveRowParams {
    serviceLine: string;
    newValue: string;
    jiraTicket: string;
    jiraPurpose: string;
    rawConfig: CMxAPI.ConfigurationValue;
}

export const useConfigurationSave = (
    configDetail: CMxAPI.Configuration | null,
    tenantId: string,
    onSaveComplete?: () => void
) => {
    const dispatch: ThunkDispatch<any, any, AnyAction> = useDispatch();

    const handleSaveRow = useCallback(
        async ({
                   serviceLine,
                   newValue,
                   jiraTicket,
                   jiraPurpose,
                   rawConfig
               }: SaveRowParams) => {
            if (!configDetail || !rawConfig) return;

            try {
                let updatePayload: any;

                // Handle tenant default case (serviceLine = "*")
                if (serviceLine === '*') {
                    const exactMatch = configDetail.values.find(
                        v => v.tenantId === tenantId && v.serviceLines?.includes('*')
                    );

                    const isCreatingNewTenantDefault = !exactMatch;

                    if (isCreatingNewTenantDefault) {
                        // Creating a new tenant default configuration
                        updatePayload = {
                            id: 0,
                            configurationId: rawConfig.configurationId,
                            configurationKeyname: rawConfig.configurationKeyname,
                            createdDate: null,
                            jiraPurpose,
                            jiraTicket,
                            jsonValue: null,
                            serviceLines: ['*'],
                            sourceType: rawConfig.sourceType,
                            tenantId,
                            updatedDate: null,
                            value: '',
                            version: rawConfig.version
                        };
                    } else {
                        // Updating existing tenant default
                        updatePayload = {
                            ...exactMatch,
                            jiraPurpose,
                            jiraTicket,
                            serviceLines: exactMatch.serviceLines
                        };
                    }
                } else {
                    // Handle service line-specific configurations
                    const canonicalServiceLine = serviceLineAliases[serviceLine] || serviceLine;

                    const exactMatch = configDetail.values.find(
                        v => v.tenantId === tenantId &&
                            serviceLineMatches(v, serviceLine, false)
                    );

                    const isCreatingNewServiceLineConfig = !exactMatch;

                    if (isCreatingNewServiceLineConfig) {
                        updatePayload = {
                            id: 0,
                            configurationId: rawConfig.configurationId,
                            configurationKeyname: rawConfig.configurationKeyname,
                            createdDate: null,
                            jiraPurpose,
                            jiraTicket,
                            jsonValue: null,
                            serviceLines: [canonicalServiceLine],
                            sourceType: rawConfig.sourceType,
                            tenantId,
                            updatedDate: null,
                            value: '',
                            version: rawConfig.version
                        };
                    } else {
                        updatePayload = {
                            ...exactMatch,
                            jiraPurpose,
                            jiraTicket,
                            serviceLines: exactMatch.serviceLines
                        };
                    }
                }

                // Handle value parsing (JSON vs string)
                if (rawConfig.jsonValue !== null && rawConfig.jsonValue !== undefined) {
                    try {
                        updatePayload.jsonValue = JSON.parse(newValue);
                        updatePayload.value = '';
                    } catch (parseError) {
                        console.error('Error parsing JSON:', parseError);
                        updatePayload.value = newValue;
                        updatePayload.jsonValue = null;
                    }
                } else {
                    updatePayload.value = newValue;
                    updatePayload.jsonValue = null;
                }

                // Dispatch the update
                await dispatch(
                    updateConfigurationValue({
                        configurationId: configDetail.id,
                        tenantId,
                        configValue: updatePayload
                    })
                );

                // Refresh data
                await dispatch(
                    fetchConfigDetail({
                        configurationId: configDetail.id,
                        tenantId
                    })
                );

                await dispatch(listConfigurations({ tenantId }));

                // Call completion callback
                onSaveComplete?.();
            } catch (error) {
                console.error('Error saving configuration:', error);
                throw error;
            }
        },
        [configDetail, dispatch, tenantId, onSaveComplete]
    );

    return { handleSaveRow };
};
